import { useCallback, useEffect, useMemo, useState } from "react";
import type { IssuePoHeader, IssuePoItem } from "../api/mrrInventoryApi";
import type { Application } from "../types";
import { useToast } from "../context/ToastContext";
import { buildIssuePoPdfHtml, downloadIssuePoPdf } from "../utils/issuePoPdf";

export function PoGenerateSuccessTicket({
  po,
  items,
  applications,
  onClose,
  onCancel,
}: {
  po: IssuePoHeader;
  items: IssuePoItem[];
  /** Dossier records used to fill annexure district / mandal / village. */
  applications?: Application[];
  onClose: () => void;
  /** When set, shows Cancel PO (marks PO inactive and removes linked unissued issue-material rows). */
  onCancel?: () => Promise<void>;
}) {
  const { showToast } = useToast();
  const [pdfBusy, setPdfBusy] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);

  const previewHtml = useMemo(() => {
    try {
      return buildIssuePoPdfHtml(po, items).html;
    } catch {
      return "";
    }
  }, [po, items]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const runDownload = useCallback(async () => {
    setPdfBusy(true);
    try {
      await downloadIssuePoPdf(po, items, { applications });
      showToast(`PDF downloaded (${po.poNumber}).`, "success");
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Could not download PDF.", "error");
    } finally {
      setPdfBusy(false);
    }
  }, [po, items, applications, showToast]);

  const runCancel = useCallback(async () => {
    if (!onCancel) return;
    const ok = window.confirm(
      `Cancel PO ${po.poNumber}? It will be marked inactive and removed from PO lists. Any issue-material lines created with this PO (not yet issued) will also be removed.`,
    );
    if (!ok) return;
    setCancelBusy(true);
    try {
      await onCancel();
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Could not cancel PO.", "error");
    } finally {
      setCancelBusy(false);
    }
  }, [onCancel, po.poNumber, showToast]);

  return (
    <div className="po-success-overlay" role="dialog" aria-modal="true" aria-labelledby="po-success-title">
      <button type="button" className="po-success-overlay__backdrop" aria-label="Dismiss" onClick={onClose} />
      <div className="po-success-modal">
        <div className="po-success-printer" aria-hidden="true">
          <div className="po-success-printer__slot" />
          <div className="po-success-printer__body" />
        </div>
        <div className="po-success-ticket-wrap">
          <article className="po-success-ticket">
          <div className="po-success-ticket__perforation" aria-hidden="true" />
          <p id="po-success-title" className="po-success-ticket__kicker">
            Purchase order issued
          </p>
          {previewHtml ? (
            <div className="po-success-ticket__doc-frame">
              <iframe
                title="Purchase order document"
                className="po-success-ticket__iframe"
                srcDoc={previewHtml}
                scrolling="yes"
              />
            </div>
          ) : (
            <p className="po-success-ticket__preview-fallback text-muted">
              Could not build the on-screen PO preview. Use Download PDF if available.
            </p>
          )}
          <div className="po-success-ticket__actions">
            <button
              type="button"
              className="btn btn--primary btn--tg btn--lg"
              disabled={pdfBusy || cancelBusy || !items.length}
              onClick={() => void runDownload()}
            >
              {pdfBusy ? "Saving…" : "Download PDF"}
            </button>
            {onCancel ? (
              <button
                type="button"
                className="btn btn--danger btn--tg btn--lg"
                disabled={pdfBusy || cancelBusy}
                onClick={() => void runCancel()}
              >
                {cancelBusy ? "Cancelling…" : "Cancel PO"}
              </button>
            ) : null}
          </div>
          <button type="button" className="btn btn--link btn--sm po-success-ticket__close" onClick={onClose}>
            Close
          </button>
        </article>
        </div>
      </div>
    </div>
  );
}
